import { useNavigate } from "@tanstack/react-router";
import useGetDatasets from "actions/quiz/useGetDatasets";
import useCreateUser from "actions/user/useCreateUser";
import { UserInfoContext, UserInfoType } from "context/UserInfoContext";
import useCharacterInput from "hooks/useCharacterInput";
import useNavigationAudio from "hooks/useNavigationAudio";
import useUserInfoNavigation from "hooks/useUserInfoNavigation";
import { useContext, useEffect, useState } from "react";
import { characters } from "utilities/characters";
import QuizLayout from "../Quiz/components/QuizLayout/QuizLayout";
import InputArea from "./components/InputArea/InputArea";
import InputSelect from "./components/InputSelect/InputSelect";

export default function Age() {
  const { setUserInfo, userInfo, page, setPage } = useContext(UserInfoContext);
  const navigate = useNavigate();
  const audio = useNavigationAudio();
  const [endQuizModal, setEndQuizModal] = useState(false);

  const { mutateAsync: createUser } = useCreateUser();
  const { data: dataSet } = useGetDatasets();

  const activeDataset = dataSet?.find((dataset) => dataset.active);
  const needsExtraUserData = !!activeDataset?.extra_user_data;

  const {
    currentPosition,
    setCurrentPosition,
    input,
    setInput,
    index,
    setIndex,
  } = useCharacterInput({
    initialInput: userInfo.age,
    maxLength: 2,
    initialPosition: 5,
  });

  useEffect(() => {
    setInput(userInfo.age);
    if (userInfo.age.length === 2 && !userInfo.age.includes("")) {
      setCurrentPosition(11);
    }
  }, [userInfo, page]);

  const handleNumberInput = (character: number) => {
    setUserInfo((prev: UserInfoType) => {
      if (prev) {
        const newValue = { ...prev };
      } else {
        return prev;
      }

      const newValue = { ...prev };
      if (newValue.age.length === 2 && !newValue.age.includes("")) {
        return newValue;
      }
      const newAge = [...newValue.age];
      newAge.splice(index, 1, character.toString());
      newValue.age = newAge;
      return newValue;
    });
  };

  const handleBackspace = () => {
    setUserInfo((prev: UserInfoType) => {
      const newValue = { ...prev };
      const newAge = [...newValue.age];
      newAge.splice(index - 1, 1, "");
      newValue.age = newAge;
      return newValue;
    });
  };

  const handleComplete = () => {
    if (userInfo.age.length === 2 && !userInfo.age.includes("")) {
      // More steps required before creating user and moving to quiz
      if (needsExtraUserData) {
        setPage((prev) => prev + 1);
        return navigate({ to: "/extra_user_info" });
      }

      // Create user and move to quiz
      createUser({
        age: parseInt(userInfo.age.join("")),
        postcode: userInfo.postcode.join(""),
        station:
          // @ts-ignore
          import.meta.env.VITE_IS_OTHER_STATION == "true" ? "low" : "high",
      }).then((r) => {
        setUserInfo((prev: UserInfoType) => {
          return { ...prev, id: r.id };
        });
      });

      navigate({ to: "/quiz" });
    }
  };

  const handleEnter = () => {
    const currentCharacter = characters[currentPosition];

    if (typeof currentCharacter === "number") {
      audio.confirmNumber.play();
      handleNumberInput(currentCharacter);
      setIndex(index + 1);
    } else if (currentCharacter === "back" && index > 0) {
      handleBackspace();
      setIndex(index - 1);
    } else if (currentCharacter === "complete") {
      audio.checkmark.play();
      handleComplete();
    }
  };

  const handleNavigateBack = () => {
    setPage(0);
    navigate({ to: "/postcode" });
  };

  const { backSelected, endQuizSelected, eventCounter } = useUserInfoNavigation(
    {
      audio,
      currentPosition,
      setCurrentPosition,
      onEnter: handleEnter,
      onNavigateBack: handleNavigateBack,
      setEndQuizModal,
      canNavigateBack: true,
    },
  );

  return (
    <QuizLayout
      title="Wat is je leeftijd?"
      titleAbsolute
      backSelected={backSelected}
      endQuizSelected={endQuizSelected}
      eventCounter={eventCounter}
      questionIndex={page}
      endQuizModal={endQuizModal}
      setEndQuizModal={setEndQuizModal}
    >
      <InputArea input={input} />
      <InputSelect
        options={characters}
        currentPosition={currentPosition}
        numberOfSteps={characters.length}
      />
    </QuizLayout>
  );
}
